import { cn } from '~/lib/utils';

/**
 * Purely PRESENTATIONAL switch visual (track + thumb). It is NOT interactive — the enclosing
 * clickable row (role="switch") owns all interaction + ARIA. It deliberately does NOT use the
 * Radix-based `~/components/ui/switch`.
 *
 * WHY (regression guard): a real Radix Switch rendered here is a nested CONTROLLED `<button>` with
 * no `onCheckedChange`. When its `checked` prop flips during the row's synchronous `setValue`
 * re-render, Radix's controlled-without-handler reconciliation re-dispatches the bubbled row click
 * → the row's onToggle re-fires → setValue → re-render → … → React "Maximum update depth exceeded".
 * Even `disabled` does not stop it (Radix still owns the button ref/reconciliation). A plain element
 * has no such behaviour, so it cannot re-dispatch the click.
 */
export function SwitchVisual({ checked, disabled }: { checked: boolean; disabled?: boolean }) {
	return (
		<span
			aria-hidden
			data-state={checked ? 'checked' : 'unchecked'}
			className={cn(
				'pointer-events-none inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent shadow-xs transition-colors',
				checked ? 'bg-primary' : 'bg-input dark:bg-input/80',
				disabled && 'opacity-50'
			)}>
			<span
				className={cn(
					'block size-4 rounded-full bg-background ring-0 transition-transform dark:bg-foreground',
					checked ? 'translate-x-[calc(100%-2px)] dark:bg-primary-foreground' : 'translate-x-0'
				)}
			/>
		</span>
	);
}
